/* The one cart (owner, 1 October 2026: "we should only have the drawer that opens on every
 * page, we would make it full screen with an x in the corner"; a wide panel on a computer).
 *
 * The cart sheet (snippets/cart-sheet.liquid) holds the cart section itself
 * (sections/cart.liquid), drawn by Shopify's Section Rendering API (?sections=cart) in the
 * visitor's language and currency, so it cannot differ from the cart page. It opens from
 * the header's cart on every page and after adding to the cart (assets/il-cart.js passes
 * the section that the add returned, so no second request is made).
 *
 * The same events serve the cart page and the sheet: quantities and removals go through
 * cart/change.js and the section comes back with the answer, drawn in place; the gift
 * tick is the order attribute Gift, with no message to type. Checkout is the section's
 * own form, sent as it is.
 *
 * After drawing, il:cart-rendered tells the extras (il-cart-extras.js) and the
 * free-shipping bar (il-ship-bar.js) that there is a new cart to serve.
 */
(function () {
  const sheet = document.getElementById('il-cart-sheet');
  const body = sheet ? sheet.querySelector('[data-sheet-body]') : null;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  let words = {};
  try { words = JSON.parse(document.getElementById('il-i18n')?.textContent || '{}'); } catch { /* plain words below */ }
  const t = (key, fallback) => words[key] || fallback;

  const onCartPage = () => /\/cart\/?$/.test(location.pathname);
  let isOpen = false;
  let lastFocus = null;
  let pushed = false;
  let afterClose = null;
  let busy = false;

  const post = (path, payload) => fetch(root + path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  });

  /* The thank-you page's one-time sign offer (app il-thankyou-offer, owner, 20 October 2026):
   * its button opens the matching sign, or the signs collection, with the name already typed
   * and ?il_offer=sign. That puts the offer's codes (theme settings offer_code_sign and
   * offer_code_shipping: a sign at EUR 29.99, free shipping) on the cart, which keeps them
   * until the sign goes in, and marks the cart with the attribute "Thank-you offer": the order
   * shows it, and the free-shipping bar (il-ship-bar.js) says the order ships free. Once per
   * visit; cart writes go one at a time (the fetch queue in layout/theme.liquid). */
  (() => {
    if (new URLSearchParams(location.search).get('il_offer') !== 'sign') return;
    let offer = {};
    try { offer = JSON.parse(document.getElementById('il-offer-codes')?.textContent || '{}'); } catch { /* no codes */ }
    const codes = [offer.sign, offer.shipping].map((c) => String(c || '').trim()).filter(Boolean).join(',');
    if (!codes) return;
    try { if (sessionStorage.getItem('il-offer') === codes) return; } catch { /* storage refused: apply again */ }
    post('cart/update.js', { discount: codes, attributes: { 'Thank-you offer': 'Sign 29.99' } })
      .then((r) => { if (r.ok) try { sessionStorage.setItem('il-offer', codes); } catch { /* fine */ } })
      .catch(() => {});
  })();

  /* ---- drawing ---- */

  /* The line's picture is the customer's own design when this browser kept one for it
     (il-cart.js keeps it at the moment of adding). A new image element: one that had a
     srcset keeps reporting that source's density after the srcset is removed. */
  const THUMB = 'il-cart-thumb:';
  function pictures(scope) {
    try {
      scope.querySelectorAll('#il-cart .item[data-key]').forEach((item) => {
        const own = localStorage.getItem(THUMB + item.dataset.key);
        const image = item.querySelector('.cthumb img');
        if (!own || !image || image.hasAttribute('data-name-thumb')) return;
        const picture = new Image(300, 225);
        picture.alt = item.dataset.thumbAlt || '';
        picture.setAttribute('data-name-thumb', '');
        picture.src = own;
        image.replaceWith(picture);
      });
    } catch { /* storage unavailable: the product images stay */ }
  }

  function counts(count) {
    if (!Number.isFinite(count)) return;
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = el.textContent.replace(/\(\d+\)/, '(' + count + ')');
    });
  }

  let extrasLoading = null;
  function extras(scope) {
    if (!scope.querySelector('[data-cart-extras]') || !sheet?.dataset.extrasSrc) return;
    if (!extrasLoading) extrasLoading = import(sheet.dataset.extrasSrc).catch(() => { extrasLoading = null; });
  }

  /* Put a drawn cart section into the sheet or the cart page's section. */
  function place(target, html, cart) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const drawn = doc.querySelector('.shopify-section') || doc.body;
    target.replaceChildren(...drawn.childNodes);
    pictures(target);
    const head = target.querySelector('[data-item-count]');
    if (head) counts(Number(head.dataset.itemCount));
    document.dispatchEvent(new CustomEvent('il:cart-rendered', { detail: { root: target, cart } }));
    extras(target);
  }

  function status(target, text, withLink) {
    const p = document.createElement('p');
    p.className = 'ilsheet__status';
    p.setAttribute('role', withLink ? 'alert' : 'status');
    p.append(text);
    if (withLink) {
      const link = document.createElement('a');
      link.href = root + 'cart';
      link.textContent = t('cart.sheet_open_page', 'Open the cart page');
      p.append(' ', link);
    }
    target.replaceChildren(p);
  }

  async function fetchSection() {
    const res = await fetch(root + '?sections=cart', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('cart section ' + res.status);
    const json = await res.json();
    if (!json || !json.cart) throw new Error('no cart section');
    return json.cart;
  }

  /* The cart page's own section: the one around the cart's head. */
  const pageSection = () => document.querySelector('main [data-item-count]')?.closest('.shopify-section') || null;

  /* ---- opening and closing ---- */

  function show() {
    if (isOpen) return;
    isOpen = true;
    lastFocus = document.activeElement;
    sheet.hidden = false;
    document.documentElement.classList.add('il-sheet-open');
    // The phone's back button closes the cart instead of leaving the page.
    try { history.pushState({ ilCartSheet: true }, ''); pushed = true; } catch { pushed = false; }
    sheet.querySelector('.ilsheet__x')?.focus({ preventScroll: true });
  }

  function hide(then) {
    if (!isOpen) return;
    isOpen = false;
    sheet.hidden = true;
    document.documentElement.classList.remove('il-sheet-open');
    if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    if (pushed) {
      pushed = false;
      afterClose = then || null;
      history.back();
    } else if (then) {
      then();
    }
  }

  window.addEventListener('popstate', () => {
    if (isOpen) {
      pushed = false;
      hide();
    }
    if (afterClose) {
      const then = afterClose;
      afterClose = null;
      then();
    }
  });

  async function open(options) {
    if (!sheet) { location.href = root + 'cart'; return; }
    show();
    if (options && options.html) {
      place(body, options.html, options.cart);
      return;
    }
    status(body, t('cart.sheet_loading', 'Loading your cart…'));
    try {
      place(body, await fetchSection());
    } catch {
      status(body, t('cart.sheet_error', 'Your cart could not be loaded here.'), true);
    }
  }

  /* ---- keeping the cart still ----
     When the cart is drawn again, what the customer pressed stays where it was on the
     screen (owner, 1 October 2026, of the priority order: "the cart should not move so they
     are closer to checkout already"). The priority line is added at the top of the lines:
     without this the Priority box and Checkout dropped by that line's height (116 px on a
     phone), and rose again when it was taken out. The place is held while the new drawing
     settles, and let go after a moment, or at once when the customer scrolls, taps or
     types. */
  let letGo = () => {};
  function holdPlace(target, selector) {
    letGo();
    const pressed = selector && target.querySelector(selector);
    if (!pressed || !pressed.getClientRects().length) return () => {};
    const top = pressed.getBoundingClientRect().top;
    const scroller = target.matches('[data-sheet-body]') ? target : null;
    const fix = () => {
      const now = target.querySelector(selector);
      if (!now || !now.getClientRects().length) return;
      const by = Math.round(now.getBoundingClientRect().top - top);
      if (!by) return;
      if (scroller) scroller.scrollTop += by;
      else window.scrollBy(0, by);
    };
    // Called once the new drawing is in: corrected before the next frame is painted (the
    // extras have shown the Priority box again by then; measuring at once, with the box
    // still hidden, would itself pull the sheet's scroll up), and whenever its size changes
    // while it settles.
    return () => {
      requestAnimationFrame(fix);
      if (!('ResizeObserver' in window)) return;
      let idle = null;
      const watcher = new ResizeObserver(() => {
        fix();
        clearTimeout(idle);
        idle = setTimeout(() => letGo(), 1500);
      });
      [...target.children].forEach((child) => watcher.observe(child));
      const quit = ['wheel', 'touchstart', 'pointerdown', 'keydown'];
      const cap = setTimeout(() => letGo(), 6000);
      const release = () => {
        watcher.disconnect();
        clearTimeout(idle);
        clearTimeout(cap);
        quit.forEach((type) => window.removeEventListener(type, release, true));
        if (letGo === release) letGo = () => {};
      };
      letGo = release;
      quit.forEach((type) => window.addEventListener(type, release, { capture: true, passive: true }));
    };
  }

  /* Draw the cart again where it is shown: the open sheet, or the cart page. `anchor`: what
     the customer pressed, kept where it is on the screen. */
  async function refresh(html, cart, anchor) {
    const target = isOpen ? body : pageSection();
    if (!target) return;
    try {
      const drawn = html || await fetchSection();
      const settle = holdPlace(target, anchor);
      place(target, drawn, cart);
      settle();
    } catch {
      if (!isOpen) location.reload();
      else status(body, t('cart.sheet_error', 'Your cart could not be loaded here.'), true);
    }
  }

  /* ---- the cart's own controls (cart page and sheet alike) ---- */

  /* The gift tick's own request, waited for before a change draws the cart again (or the
     cart would come back unticked). */
  let giftSaving = Promise.resolve();

  async function change(from, line, quantity, focusSelector) {
    if (busy || !line) return;
    busy = true;
    const target = from.closest('[data-sheet-body]') || from.closest('.shopify-section');
    target?.setAttribute('aria-busy', 'true');
    try {
      await giftSaving;
      const res = await post('cart/change.js', { line, quantity, sections: 'cart', sections_url: location.pathname });
      if (!res.ok) throw new Error('change ' + res.status);
      const cart = await res.json();
      if (target) {
        const drawn = (cart.sections && cart.sections.cart) || await fetchSection();
        // The button pressed stays under the finger (a 5% line appearing above it would
        // otherwise push it down).
        const settle = holdPlace(target, focusSelector);
        place(target, drawn, cart);
        settle();
      }
      document.dispatchEvent(new CustomEvent('il:cart-contents', { detail: { cart } }));
      const again = focusSelector && target?.querySelector(focusSelector);
      if (again) again.focus({ preventScroll: true });
    } catch {
      // Without the answer, the page's own way: the cart page, read from the server.
      location.href = root + 'cart';
    } finally {
      busy = false;
      target?.removeAttribute('aria-busy');
    }
  }

  /* "Add another name" on the page of the same product: the cart closes, the name empties
     (the colours stay) and the name field takes the focus. Elsewhere it is a link. */
  const handleOf = (path) => (String(path).match(/\/products\/([^/?#]+)/) || [])[1] || '';
  function resetName() {
    const field = document.querySelector('[data-text-input][form="il-buy"]');
    if (!field) return;
    field.value = '';
    field.dispatchEvent(new Event('input', { bubbles: true }));
    const url = new URL(location.href);
    if (url.searchParams.has('preview_name')) {
      url.searchParams.delete('preview_name');
      history.replaceState(history.state, '', url);
    }
    field.scrollIntoView({ block: 'center', behavior: 'smooth' });
    field.focus({ preventScroll: true });
  }

  document.addEventListener('click', (e) => {
    const qty = e.target.closest('#il-cart [data-qty]');
    if (qty) {
      e.preventDefault();
      const item = qty.closest('.item');
      const now = Number(item?.querySelector('[data-qty-input]')?.value || 0);
      const next = Math.max(0, now + Number(qty.dataset.qty));
      const line = Number(qty.dataset.line);
      change(qty, line, next, '[data-qty="' + qty.dataset.qty + '"][data-line="' + line + '"]');
      return;
    }

    const remove = e.target.closest('#il-cart a[href*="/cart/change"]');
    if (remove) {
      e.preventDefault();
      change(remove, Number(remove.closest('[data-line]')?.dataset.line), 0);
      return;
    }

    const another = e.target.closest('[data-another-name]');
    if (another && isOpen && document.querySelector('[data-text-input][form="il-buy"]') &&
        handleOf(new URL(another.href, location.href).pathname) === handleOf(location.pathname)) {
      e.preventDefault();
      hide(resetName);
      return;
    }

    if (e.target.closest('[data-sheet-close]')) {
      hide();
      return;
    }

    // The header's cart and the phone menu's: the sheet, except on the cart page itself.
    const opener = e.target.closest('a[data-cart-count]');
    if (opener && sheet && !onCartPage() && e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
      e.preventDefault();
      document.querySelector('#il-menu[data-open] [data-menu-close]')?.click();
      open();
    }
  });

  /* The tick goes on the order as it is pressed: Gift: Yes (an empty value removes it).
     There is nothing to type (owner, 1 October 2026). */
  document.addEventListener('change', (e) => {
    const toggle = e.target.closest('#il-cart [data-gift-toggle]');
    if (!toggle) return;
    giftSaving = post('cart/update.js', { attributes: { Gift: toggle.checked ? 'Yes' : '' } }).catch(() => {});
  });

  /* The keyboard: Escape closes, and Tab stays inside the open cart. */
  document.addEventListener('keydown', (e) => {
    if (!isOpen) return;
    if (e.key === 'Escape') { hide(); return; }
    if (e.key !== 'Tab') return;
    const focusable = [...sheet.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), textarea, select')]
      .filter((el) => el.getClientRects().length);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  /* The extras changed the cart (the priority service): draw it again, the Priority box
     where it was. */
  document.addEventListener('il:cart-changed', (e) => refresh(null, e.detail && e.detail.cart, e.detail && e.detail.anchor));

  /* Back to a page kept by the browser with the sheet open: start closed. */
  window.addEventListener('pageshow', (e) => {
    if (e.persisted && isOpen && !(history.state && history.state.ilCartSheet)) {
      pushed = false;
      hide();
    }
  });

  window.ilCartSheet = { open, close: () => hide(), refresh };
})();
